/**
 * Servicios y áreas de práctica del estudio. Fuente única: alimenta /servicios,
 * el resumen de la home, el schema (OfferCatalog) y las opciones del formulario
 * de contacto.
 *
 * Modelo:
 *   serviceSegments — 3 segmentos de cliente, cada uno con copy umbrella.
 *   practiceAreas   — ~10 áreas de práctica, cada una asignada a un segmento.
 *
 * ESM puro — sin imports de Next.
 */

export const serviceSegments = [
  {
    id: 'empresas',
    number: '01',
    title: 'Servicios legales para empresas',
    image: '/assets/services/empresas.webp',
    // Título corto y resumen para la tarjeta de la home.
    shortTitle: 'Empresas',
    blurb:
      'Acompañamiento jurídico permanente para el día a día del negocio: coordinamos tributario, corporativo, laboral, compliance, licitaciones y propiedad intelectual con un enfoque preventivo.',
    intro:
      'Entendemos que las empresas requieren mucho más que respuestas jurídicas aisladas. Por ello, acompañamos a nuestros clientes de manera permanente, coordinando distintas especialidades para entregar soluciones oportunas que apoyen el crecimiento, mitiguen riesgos y aporten valor a la gestión del negocio en su día a día.',
  },
  {
    id: 'familias',
    number: '02',
    title: 'Planificación patrimonial y sucesoria',
    image: '/assets/services/familias.webp',
    shortTitle: 'Familias y patrimonio',
    blurb:
      'Protección del patrimonio familiar y transición ordenada entre generaciones: testamentos, herencias, donaciones, particiones y sociedades familiares, con eficiencia tributaria.',
    intro:
      'Las decisiones sobre el patrimonio familiar trascienden una generación. Una planificación adecuada permite proteger los activos, optimizar su estructura jurídica y tributaria, prevenir contingencias y asegurar una transición ordenada hacia las futuras generaciones.',
  },
  {
    id: 'organizaciones',
    number: '03',
    title:
      'Servicios legales integrales para fundaciones, corporaciones y asociaciones gremiales',
    image: '/assets/services/organizaciones.webp',
    shortTitle: 'Fundaciones y corporaciones',
    blurb:
      'Asesoría integral para organizaciones sin fines de lucro: constitución, gobierno corporativo, cumplimiento tributario, compliance y acompañamiento en fiscalizaciones.',
    intro:
      'Las organizaciones sin fines de lucro cumplen un rol fundamental en el desarrollo de la sociedad y enfrentan desafíos jurídicos, tributarios y de cumplimiento cada vez más exigentes. Acompañamos a fundaciones, corporaciones, asociaciones gremiales y otras organizaciones en todas las etapas de su desarrollo, con una asesoría integral que les permita cumplir con sus obligaciones legales, fortalecer su gobierno corporativo y desarrollar sus proyectos con seguridad y transparencia.',
  },
];

export const practiceAreas = [
  // ── Segmento: Empresas ──────────────────────────────────────────
  {
    slug: 'asesoria-juridica-permanente',
    segment: 'empresas',
    title: 'Asesoría Jurídica Permanente',
    summary:
      'Las empresas enfrentan diariamente decisiones que requieren respaldo jurídico oportuno. Nuestro servicio de asesoría legal permanente permite contar con un equipo de abogados disponible para resolver consultas, revisar documentos legales, apoyar negociaciones y acompañar la toma de decisiones en materias comerciales, societarias, laborales, tributarias y regulatorias.',
    closing:
      'Nos integramos al negocio de nuestros clientes, comprendiendo su realidad y convirtiéndonos en un apoyo estratégico para su crecimiento.',
  },
  {
    slug: 'derecho-tributario',
    segment: 'empresas',
    title: 'Derecho Tributario',
    summary:
      'Las decisiones tributarias requieren un análisis técnico, pero también una comprensión profunda del negocio, del patrimonio y de los objetivos de cada cliente.',
    comprende: [
      'Planificación tributaria nacional e internacional.',
      'Reorganizaciones empresariales.',
      'Estructuración y reestructuración de inversiones.',
      'Constitución y modificación de sociedades.',
      'Planificación patrimonial y sucesoria.',
      'Cumplimiento tributario y fiscalizaciones.',
      'Procesos ante el Servicio de Impuestos Internos y resolución de controversias tributarias.',
    ],
    closing:
      'Nuestro enfoque combina excelencia técnica con una visión estratégica, permitiendo diseñar estructuras eficientes, seguras y alineadas con los objetivos de largo plazo de nuestros clientes.',
  },
  {
    slug: 'derecho-corporativo-y-comercial',
    segment: 'empresas',
    title: 'Derecho Corporativo y Comercial',
    summary:
      'Acompañamos a empresas en todas las etapas de su desarrollo, desde su constitución hasta procesos de crecimiento, reorganización, inversión o adquisición.',
    comprende: [
      'Estructuración y negociación de operaciones comerciales y societarias.',
      'Elaboración y revisión de contratos.',
      'Modificaciones societarias y pactos de accionistas.',
      'Gobierno corporativo.',
      'Fusiones y adquisiciones (M&A).',
      'Procesos de due diligence.',
    ],
  },
  {
    slug: 'contratacion-publica-y-licitaciones',
    segment: 'empresas',
    title: 'Contratación Pública y Licitaciones',
    summary:
      'Las licitaciones representan importantes oportunidades de crecimiento para las empresas, pero también exigen un adecuado cumplimiento de requisitos legales y administrativos. Asesoramos a nuestros clientes durante todo el proceso de contratación pública y privada, desde el análisis de bases hasta la ejecución contractual y la resolución de controversias.',
    comprende: [
      'Participación en licitaciones públicas y privadas.',
      'Revisión de bases administrativas y técnicas.',
      'Elaboración y revisión de contratos.',
      'Observaciones e impugnaciones.',
      'Asesoría durante la ejecución contractual.',
      'Negociación de modificaciones contractuales.',
      'Resolución de controversias derivadas de contratos.',
    ],
  },
  {
    slug: 'derecho-laboral-para-empresas',
    segment: 'empresas',
    title: 'Derecho Laboral para Empresas',
    summary:
      'Asesoramos a empresas en la correcta gestión de sus relaciones laborales, buscando prevenir contingencias y fortalecer el cumplimiento normativo.',
    comprende: [
      'Contratación de trabajadores, contratos y anexos.',
      'Reglamentos internos.',
      'Investigaciones laborales y procedimientos derivados de la Ley Karin.',
      'Desvinculaciones.',
      'Negociación colectiva.',
      'Representación frente a la Dirección del Trabajo y tribunales laborales.',
    ],
  },
  {
    slug: 'compliance-y-gestion-de-riesgos',
    segment: 'empresas',
    title: 'Compliance y Gestión de Riesgos',
    summary:
      'Entendemos el compliance como una herramienta de gestión que permite prevenir riesgos, fortalecer la toma de decisiones y proteger la continuidad de las organizaciones. Asesoramos a empresas en el diseño, implementación y fortalecimiento de programas de cumplimiento y sistemas de gobierno corporativo, adaptados a su realidad, industria y nivel de riesgo.',
    comprende: [
      'Diseño, implementación y actualización de Modelos de Prevención de Delitos (Ley N° 20.393 y Ley N° 21.595).',
      'Implementación de sistemas de prevención para sujetos obligados ante la Unidad de Análisis Financiero (UAF).',
      'Elaboración y actualización de códigos de ética, políticas internas y protocolos corporativos.',
      'Diseño e implementación de canales de denuncia y procedimientos de investigación interna.',
      'Identificación y evaluación de riesgos mediante matrices de riesgos legales y de compliance.',
      'Due diligence de cumplimiento en procesos de inversión, adquisición y reorganización.',
      'Capacitaciones para directores, ejecutivos y trabajadores.',
      'Asesoría en el cumplimiento de la normativa sobre protección de datos personales.',
      'Asesoría permanente al Encargado de Prevención de Delitos y apoyo en el funcionamiento del sistema de cumplimiento.',
    ],
  },
  {
    slug: 'propiedad-intelectual-e-industrial',
    segment: 'empresas',
    title: 'Propiedad Intelectual e Industrial',
    summary:
      'La marca, el nombre comercial y otros activos intangibles constituyen parte del valor de una empresa y requieren una adecuada protección jurídica. Asesoramos a empresas y emprendedores en la protección de sus activos de propiedad industrial, acompañándolos durante todo el proceso ante el Instituto Nacional de Propiedad Industrial (INAPI).',
    comprende: [
      'Registro de marcas comerciales.',
      'Renovación de marcas.',
      'Oposiciones y defensa administrativa ante INAPI.',
      'Recursos administrativos.',
      'Cesión y licenciamiento de marcas.',
      'Protección de activos intangibles.',
      'Estrategias de protección marcaria.',
    ],
  },
  {
    slug: 'derecho-inmobiliario',
    segment: 'empresas',
    title: 'Derecho Inmobiliario',
    summary:
      'Asesoramos a empresas e inversionistas en la estructuración jurídica de operaciones inmobiliarias, compraventas de bienes raíces, desarrollo de proyectos y negociación de acuerdos, compatibilizando los objetivos del negocio con una adecuada mitigación de riesgos.',
    comprende: [
      'Adquisición y venta de bienes raíces.',
      'Estructuración jurídica y financiamiento de proyectos inmobiliarios.',
      'Desarrollo, construcción y comercialización.',
      'Elaboración y negociación de contratos inmobiliarios.',
      'Regularización de bienes inmuebles.',
      'Due diligence inmobiliario.',
    ],
  },
  {
    slug: 'litigacion-y-resolucion-de-controversias',
    segment: 'empresas',
    title: 'Litigación y Resolución de Controversias',
    summary:
      'Acompañamos a empresas y personas en la resolución estratégica de controversias civiles y laborales, con representación judicial y extrajudicial, definición de estrategias procesales y negociación de acuerdos que privilegien soluciones sostenibles.',
    comprende: [
      'Litigios civiles y comerciales.',
      'Litigios laborales.',
      'Representación ante tribunales de justicia.',
      'Negociación y acuerdos extrajudiciales.',
      'Definición de estrategias procesales.',
    ],
  },
  {
    slug: 'empresas-familiares',
    segment: 'empresas',
    title: 'Empresas Familiares',
    summary:
      'Las empresas familiares enfrentan desafíos únicos, donde las decisiones empresariales, patrimoniales y familiares se encuentran estrechamente vinculadas. Una adecuada planificación resulta esencial para preservar el patrimonio, asegurar la continuidad del negocio y facilitar una transición ordenada entre generaciones.',
    comprende: [
      'Reorganización patrimonial y societaria.',
      'Protocolos familiares.',
      'Planificación sucesoria.',
      'Estructuras de gobierno corporativo.',
      'Acuerdos entre socios.',
    ],
    closing:
      'Nuestra práctica integra el derecho corporativo, tributario, patrimonial y sucesorio para desarrollar soluciones acordes con la realidad y los objetivos de cada familia empresaria.',
  },

  // ── Segmento: Familias ──────────────────────────────────────────
  {
    slug: 'planificacion-patrimonial-y-sucesoria',
    segment: 'familias',
    title: 'Planificación Patrimonial y Sucesoria',
    summary:
      'Una planificación adecuada permite proteger los activos, optimizar su estructura jurídica y tributaria, prevenir contingencias y asegurar una transición ordenada hacia las futuras generaciones. Asesoramos a personas, familias y grupos familiares en el diseño e implementación de estrategias patrimoniales que resguarden su patrimonio y otorguen certeza jurídica.',
    comprende: [
      'Planificación patrimonial y sucesoria.',
      'Elaboración y revisión de testamentos.',
      'Herencias y posesiones efectivas.',
      'Donaciones, usufructos y otras formas de transmisión patrimonial.',
      'Particiones y acuerdos entre herederos.',
      'Reorganización patrimonial y societaria.',
      'Constitución y estructuración de sociedades familiares.',
      'Protocolos familiares.',
      'Regularización de bienes inmuebles.',
      'Planificación tributaria asociada a herencias, donaciones y sucesiones.',
    ],
    closing:
      'Cada patrimonio es distinto y cada familia enfrenta desafíos propios. Por ello, desarrollamos soluciones a la medida de cada cliente, con un enfoque preventivo y de largo plazo, orientado a proteger el patrimonio familiar, reducir contingencias y facilitar su adecuada administración y transmisión.',
  },

  // ── Segmento: Fundaciones, corporaciones y asociaciones gremiales ─
  {
    slug: 'fundaciones-corporaciones-asociaciones',
    segment: 'organizaciones',
    title: 'Fundaciones, Corporaciones y Asociaciones Gremiales',
    summary:
      'Acompañamos a fundaciones, corporaciones, asociaciones gremiales y otras organizaciones en todas las etapas de su desarrollo, entregando una asesoría integral que les permita cumplir adecuadamente con sus obligaciones legales, fortalecer su gobierno corporativo y desarrollar sus proyectos con seguridad y transparencia.',
    comprende: [
      'Constitución de fundaciones, corporaciones y asociaciones gremiales.',
      'Modificaciones de estatutos y procesos de adecuación normativa.',
      'Obtención de personalidad jurídica y tramitación ante organismos públicos.',
      'Asesoría tributaria para entidades sin fines de lucro.',
      'Gobierno corporativo y funcionamiento de directorios.',
      'Elaboración y revisión de reglamentos internos.',
      'Compliance y programas de cumplimiento.',
      'Implementación de Modelos de Prevención de Delitos orientado a fundaciones.',
      'Códigos de ética y canales de denuncia.',
      'Gestión de riesgos e investigaciones internas.',
      'Revisión de contratos y asesoría legal permanente.',
      'Acompañamiento en procesos de fiscalización y cumplimiento regulatorio.',
    ],
    closing:
      'Nuestro objetivo es que las organizaciones puedan concentrarse en el cumplimiento de su misión, contando con el respaldo de un equipo jurídico que las acompañe en la toma de decisiones y en el fortalecimiento de su institucionalidad.',
  },
];

// Áreas agrupadas por segmento (para render).
export const areasBySegment = (segmentId) =>
  practiceAreas.filter((a) => a.segment === segmentId);

// Grilla de áreas de práctica (sección de la home, estilo del prototipo):
// número + título + una línea. Enlaza a /servicios#<slug> para el detalle.
const AREA_SHORT = {
  'asesoria-juridica-permanente':
    'Un equipo disponible para el día a día del negocio.',
  'derecho-tributario': 'Planificación, cumplimiento y controversias fiscales.',
  'derecho-corporativo-y-comercial':
    'Contratos, operaciones societarias, M&A y gobierno corporativo.',
  'contratacion-publica-y-licitaciones':
    'Participación y ejecución en licitaciones públicas y privadas.',
  'derecho-laboral-para-empresas':
    'Relaciones laborales, Ley Karin y representación ante tribunales.',
  'compliance-y-gestion-de-riesgos':
    'Modelos de prevención de delitos y programas de cumplimiento.',
  'propiedad-intelectual-e-industrial':
    'Registro, renovación y defensa de marcas ante INAPI.',
  'derecho-inmobiliario':
    'Compraventas, desarrollo de proyectos y contratos inmobiliarios.',
  'litigacion-y-resolucion-de-controversias':
    'Controversias civiles y laborales, con representación judicial y acuerdos.',
  'empresas-familiares':
    'Estructura, protocolos y continuidad del patrimonio familiar.',
  'planificacion-patrimonial-y-sucesoria':
    'Testamentos, herencias, donaciones y sociedades familiares.',
  'fundaciones-corporaciones-asociaciones':
    'Constitución, gobierno y cumplimiento para el tercer sector.',
};

export const practiceAreasGrid = practiceAreas.map((a, i) => ({
  slug: a.slug,
  title: a.title,
  number: String(i + 1).padStart(2, '0'),
  short: AREA_SHORT[a.slug] || `${a.summary.split('. ')[0]}.`,
}));

// Opciones del <select> "Área de interés" del formulario de contacto.
export const contactAreaOptions = [
  ...practiceAreas.map((a) => a.title),
  'Otra',
];
